import type { components } from '@/lib/api/generated/schema'

export type Schemas = components['schemas']
export type Notification = Schemas['Notification']
export type NotificationType = Schemas['NotificationType']
export type PushPreferences = Schemas['PushPreferences']
export type PushPreferenceKey = keyof PushPreferences
export type ProximitySettings = Schemas['ProximitySettings']

/**
 * Frozen preference order from API_CONTRACT §7.1, UI_PARITY §3.15 and the
 * upstream 0029 addition. `activity_updated` belongs to `activity_updates`;
 * the list is display order, not a new contract.
 */
export const PUSH_PREFERENCE_KEYS = [
  'messages',
  'friendships',
  'participation',
  'activity_updates',
  'feedback',
  'nearby_activities',
  'other',
] as const satisfies readonly PushPreferenceKey[]

/**
 * Message notifications are surfaced by the chat badges/read cursors, not by
 * the inbox (UI_PARITY §3.15 and BE-NOTIFICATIONS decision 1). The backend
 * already filters them; this guard keeps a stale/legacy response out of the
 * feed as well.
 */
const MESSAGE_NOTIFICATION_TYPES: ReadonlySet<NotificationType> = new Set([
  'private_message',
  'activity_chat_message',
])

export function isInboxNotification(notification: Notification): boolean {
  return !MESSAGE_NOTIFICATION_TYPES.has(notification.type)
}

export function isUnread(notification: Notification): boolean {
  return notification.readAt === null
}

/**
 * Validates the stored notification row carried by a realtime `notification`
 * frame. Unknown extra fields are allowed; malformed frames are ignored and
 * the normal REST fallback reconciles the inbox/badge.
 */
export function parseNotification(value: unknown): Notification | null {
  if (typeof value !== 'object' || value === null) return null
  const candidate = value as Record<string, unknown>
  if (
    typeof candidate.id !== 'string' ||
    candidate.id.length === 0 ||
    typeof candidate.type !== 'string' ||
    typeof candidate.title !== 'string' ||
    typeof candidate.channelPush !== 'boolean' ||
    typeof candidate.createdAt !== 'string' ||
    !('readAt' in candidate) ||
    (candidate.readAt !== null && typeof candidate.readAt !== 'string')
  ) {
    return null
  }
  const payload = candidate.payload
  if (
    payload !== undefined &&
    payload !== null &&
    (typeof payload !== 'object' || Array.isArray(payload))
  ) {
    return null
  }
  return {
    id: candidate.id,
    type: candidate.type as Notification['type'],
    title: candidate.title,
    body: typeof candidate.body === 'string' ? candidate.body : null,
    payload:
      payload && typeof payload === 'object'
        ? (payload as Record<string, unknown>)
        : {},
    channelPush: candidate.channelPush,
    readAt: candidate.readAt as string | null,
    createdAt: candidate.createdAt,
  }
}
